import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';

import { formatAmountWithCurrency, formatExchangeRate } from '../../../helpers/commonHelper';
import newUserAppUrl from '../../../routing/newUserApp';

export const CurrencyDetailsModal = ({ isOpen, onClose, breakdown, title, total, prefferedCurrency }) => {
  const { t } = useTranslation('common');
  const modalRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div ref={modalRef} className="bg-white rounded-xl w-[480px] max-h-[90vh] overflow-hidden">
        <div className="p-3 border-b border-t-0 border-r-0 border-l-0 border-gray-100 border-solid flex items-center justify-between">
          <h3 className="text-lg font-semibold mb-0">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer bg-transparent border-0 p-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-3">
          <div className="space-y-4">
            {breakdown.map(({ currency, amount, exchange_rate }) => (
              <div key={currency} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-sm font-medium">{currency}</span>
                    </div>
                    <div className="text-lg font-medium">{formatAmountWithCurrency(amount, currency)}</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatAmountWithCurrency(amount * exchange_rate, prefferedCurrency)}
                  </div>
                </div>
                {prefferedCurrency !== currency && (
                  <div className="text-sm text-gray-500 flex items-center justify-between">
                    <span>{t('currency.exchangeRate')}</span>
                    <span>
                      1 {currency} = {formatExchangeRate(exchange_rate, prefferedCurrency)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 pt-3 border-t border-b-0 border-r-0 border-l-0 border-gray-100 border-solid">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-600">
                {t('currency.total', { currency: prefferedCurrency })}
              </div>
              <div className="text-xl font-semibold">{total}</div>
            </div>

            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3 flex gap-3">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                {t('currency.boonPayInfo')}{' '}
                <a
                  href={`${newUserAppUrl}/payments/settings`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {t('currency.rewardPayoutSettings')}
                </a>
                . {t('currency.noPayoutMethod')}
                <br />
                <br />
                {t('currency.internalPaymentInfo')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
